import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export type OpportunityStatus = 'open' | 'won' | 'lost';
export type DateFilterPeriod = '30d' | '60d' | '90d' | 'all';

export interface Opportunity {
  id: string;
  account_id: string;
  pipeline_id: string;
  stage_id: string;
  contact_id?: string;
  title: string;
  product?: string;
  price: number;
  tags: string[];
  order_index: number;
  contact_tags?: string[];
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  attributed_user_id?: string | null;
  attributed_user_name?: string | null;
  status: OpportunityStatus;
  loss_reason_id?: string | null;
  loss_reason_name?: string | null;
  closed_at?: string | null;
  sale_date?: string | null;
  won_by_user_id?: string | null;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface WonOpportunityData {
  product?: string;
  price?: number;
  sale_date?: string;
  won_by_user_id?: string;
  notes?: string;
}

const getDateFilterValue = (period: DateFilterPeriod): string | null => {
  if (period === 'all') return null;
  const days = parseInt(period);
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString();
};

export function useOpportunities(
  pipelineId?: string,
  statusFilter: OpportunityStatus | 'all' = 'open',
  dateFilter: DateFilterPeriod = '30d'
) {
  const { user } = useAuth();
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingProgress, setLoadingProgress] = useState<{
    loaded: number;
    total: number;
    isLoadingMore: boolean;
  }>({ loaded: 0, total: 0, isLoadingMore: false });

  const fetchOpportunities = useCallback(async () => {
    if (!user?.account?.id || !supabase) return;

    try {
      setLoading(true);
      setLoadingProgress({ loaded: 0, total: 0, isLoadingMore: false });

      const dateFilterValue = getDateFilterValue(dateFilter);
      const INITIAL_BATCH = dateFilter === 'all' ? 100 : 500;
      const BATCH_SIZE = 250;
      const BATCH_DELAY = 150;

      let countQuery = supabase
        .from('account_opportunities')
        .select('*', { count: 'exact', head: true })
        .eq('account_id', user.account.id);

      if (pipelineId) {
        countQuery = countQuery.eq('pipeline_id', pipelineId);
      }

      if (statusFilter !== 'all') {
        countQuery = countQuery.eq('status', statusFilter);
      }

      if (dateFilterValue) {
        countQuery = countQuery.gte('created_at', dateFilterValue);
      }

      const { count: totalCount } = await countQuery;
      const total = totalCount || 0;

      // Carregar usuários uma única vez (será reutilizado)
      const { data: usersData } = await supabase
        .from('account_users')
        .select('auth_user_id, name')
        .eq('account_id', user.account.id);

      const usersMap = new Map(usersData?.map(u => [u.auth_user_id, u.name]) || []);

      // Função para transformar dados
      const transformData = (items: any[]) => items.map((item: any) => ({
        ...item,
        contact_tags: item.contact?.tags || [],
        contact_name: item.contact?.name || item.contact?.display_name || item.contact?.push_name_last || 'Contato sem nome',
        contact_phone: item.contact?.phone,
        contact_email: item.contact?.email || '',
        attributed_user_id: item.contact?.attribution_id || null,
        attributed_user_name: item.contact?.attribution_id ? usersMap.get(item.contact.attribution_id) || null : null,
        loss_reason_name: item.loss_reason?.name || null,
        contact: undefined,
        loss_reason: undefined
      }));

      let initialQuery = supabase
        .from('account_opportunities')
        .select(`
          *,
          contact:account_contacts(
            name,
            display_name,
            push_name_last,
            phone,
            email,
            tags,
            attribution_id
          ),
          loss_reason:account_loss_reasons(
            name
          )
        `)
        .eq('account_id', user.account.id)
        .range(0, INITIAL_BATCH - 1)
        .order('created_at', { ascending: false });

      if (pipelineId) {
        initialQuery = initialQuery.eq('pipeline_id', pipelineId);
      }

      if (statusFilter !== 'all') {
        initialQuery = initialQuery.eq('status', statusFilter);
      }

      if (dateFilterValue) {
        initialQuery = initialQuery.gte('created_at', dateFilterValue);
      }

      const { data: initialData, error: initialError } = await initialQuery;

      if (initialError) throw initialError;

      // Mostrar primeiro lote imediatamente
      const transformedInitial = transformData(initialData || []);
      setOpportunities(transformedInitial);
      setLoading(false);
      setLoadingProgress({
        loaded: transformedInitial.length,
        total,
        isLoadingMore: total > INITIAL_BATCH
      });

      // PASSO 2: Se houver mais dados, carregar em background
      if (total > INITIAL_BATCH) {
        let offset = INITIAL_BATCH;
        let allOpportunities = [...(initialData || [])];

        while (offset < total) {
          // Delay entre requisições para não sobrecarregar
          await new Promise(resolve => setTimeout(resolve, BATCH_DELAY));

          let query = supabase
            .from('account_opportunities')
            .select(`
              *,
              contact:account_contacts(
                name,
                display_name,
                push_name_last,
                phone,
                tags,
                attribution_id
              ),
              loss_reason:account_loss_reasons(
                name
              )
            `)
            .eq('account_id', user.account.id)
            .range(offset, offset + BATCH_SIZE - 1)
            .order('created_at', { ascending: false });

          if (pipelineId) {
            query = query.eq('pipeline_id', pipelineId);
          }

          if (statusFilter !== 'all') {
            query = query.eq('status', statusFilter);
          }

          if (dateFilterValue) {
            query = query.gte('created_at', dateFilterValue);
          }

          const { data: batchData, error: batchError } = await query;

          if (batchError) {
            console.error('Erro ao carregar lote:', batchError);
            break;
          }

          if (batchData && batchData.length > 0) {
            allOpportunities = [...allOpportunities, ...batchData];
            const transformed = transformData(allOpportunities);

            setOpportunities(transformed);
            setLoadingProgress({
              loaded: transformed.length,
              total,
              isLoadingMore: transformed.length < total
            });

            offset += batchData.length;

            // Se retornou menos que BATCH_SIZE, não há mais dados
            if (batchData.length < BATCH_SIZE) {
              break;
            }
          } else {
            break;
          }
        }

        // Finalizar carregamento
        setLoadingProgress({
          loaded: allOpportunities.length,
          total: allOpportunities.length,
          isLoadingMore: false
        });
      }
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  }, [user?.account?.id, pipelineId, statusFilter, dateFilter]);

  const createOpportunity = async (opportunity: Omit<Opportunity, 'id' | 'account_id' | 'created_at' | 'updated_at' | 'order_index'>) => {
    if (!user?.account?.id || !supabase) return null;

    try {
      const { data: inserted, error: createError } = await supabase
        .from('account_opportunities')
        .insert({
          account_id: user.account.id,
          ...opportunity
        })
        .select()
        .single();

      if (createError) throw createError;
      if (!inserted) return null;

      const { data: fullData } = await supabase
        .from('account_opportunities')
        .select(`
          *,
          contact:account_contacts(
            name,
            display_name,
            push_name_last,
            phone,
            email,
            tags,
            attribution_id
          ),
          loss_reason:account_loss_reasons(
            name
          )
        `)
        .eq('id', inserted.id)
        .maybeSingle();

      if (fullData) {
        const { data: usersData } = await supabase
          .from('account_users')
          .select('auth_user_id, name')
          .eq('account_id', user.account.id);

        const usersMap = new Map(usersData?.map(u => [u.auth_user_id, u.name]) || []);

        const transformed: Opportunity = {
          ...fullData,
          contact_tags: fullData.contact?.tags || [],
          contact_name: fullData.contact?.name || fullData.contact?.display_name || fullData.contact?.push_name_last || 'Contato sem nome',
          contact_phone: fullData.contact?.phone,
          attributed_user_id: fullData.contact?.attribution_id || null,
          attributed_user_name: fullData.contact?.attribution_id ? usersMap.get(fullData.contact.attribution_id) || null : null,
          loss_reason_name: fullData.loss_reason?.name || null,
          contact: undefined,
          loss_reason: undefined
        } as Opportunity;

        if (!pipelineId || transformed.pipeline_id === pipelineId) {
          setOpportunities(prev => [transformed, ...prev]);
        }

        return transformed;
      }

      return inserted;
    } catch (err: any) {
      setError(err.message);
      return null;
    }
  };

  const checkExistingOpportunity = async (contactId: string, pipelineId: string) => {
    if (!user?.account?.id || !supabase) return null;

    try {
      const { data, error: checkError } = await supabase
        .from('account_opportunities')
        .select('id, title, stage_id')
        .eq('account_id', user.account.id)
        .eq('contact_id', contactId)
        .eq('pipeline_id', pipelineId)
        .maybeSingle();

      if (checkError) throw checkError;
      return data;
    } catch (err: any) {
      setError(err.message);
      return null;
    }
  };

  const updateOpportunity = async (id: string, updates: Partial<Opportunity>) => {
    if (!supabase) return false;

    try {
      const { error: updateError } = await supabase
        .from('account_opportunities')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (updateError) throw updateError;
      await fetchOpportunities();
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  const deleteOpportunity = async (id: string) => {
    if (!supabase) return false;

    try {
      const { error: deleteError } = await supabase
        .from('account_opportunities')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;
      await fetchOpportunities();
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  const moveOpportunity = async (opportunityId: string, newStageId: string, orderIndex?: number) => {
    if (!supabase) return false;

    try {
      const updateData: Record<string, unknown> = {
        stage_id: newStageId,
        updated_at: new Date().toISOString()
      };
      if (orderIndex !== undefined) {
        updateData.order_index = orderIndex;
      }

      const { error: updateError } = await supabase
        .from('account_opportunities')
        .update(updateData)
        .eq('id', opportunityId);

      if (updateError) throw updateError;

      setOpportunities(prev =>
        prev.map(opp =>
          opp.id === opportunityId
            ? {
                ...opp,
                stage_id: newStageId,
                updated_at: new Date().toISOString(),
                ...(orderIndex !== undefined ? { order_index: orderIndex } : {})
              }
            : opp
        )
      );

      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  const closeOpportunity = async (
    opportunityId: string,
    newStageId: string,
    status: 'won' | 'lost',
    lossReasonId?: string,
    wonData?: WonOpportunityData
  ) => {
    if (!supabase) return false;

    try {
      const closedAt = new Date().toISOString();
      const updatedAt = new Date().toISOString();
      const updateData: Record<string, unknown> = {
        stage_id: newStageId,
        status,
        closed_at: closedAt,
        updated_at: updatedAt
      };

      if (status === 'lost' && lossReasonId) {
        updateData.loss_reason_id = lossReasonId;
      }

      if (status === 'won' && wonData) {
        if (wonData.product !== undefined) updateData.product = wonData.product;
        if (wonData.price !== undefined) updateData.price = wonData.price;
        if (wonData.sale_date) updateData.sale_date = wonData.sale_date;
        if (wonData.won_by_user_id) updateData.won_by_user_id = wonData.won_by_user_id;
        if (wonData.notes !== undefined) updateData.notes = wonData.notes;
      }

      const { error: updateError } = await supabase
        .from('account_opportunities')
        .update(updateData)
        .eq('id', opportunityId);

      if (updateError) throw updateError;

      setOpportunities(prev =>
        prev.map(opp =>
          opp.id === opportunityId
            ? {
                ...opp,
                stage_id: newStageId,
                status,
                closed_at: closedAt,
                updated_at: updatedAt,
                loss_reason_id: status === 'lost' ? lossReasonId : null,
                ...(status === 'won' && wonData ? {
                  product: wonData.product ?? opp.product,
                  price: wonData.price ?? opp.price,
                  sale_date: wonData.sale_date ?? null,
                  won_by_user_id: wonData.won_by_user_id ?? null,
                  notes: wonData.notes ?? opp.notes
                } : {})
              }
            : opp
        )
      );

      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  const reopenOpportunity = async (opportunityId: string, newStageId: string) => {
    if (!supabase) return false;

    try {
      const updatedAt = new Date().toISOString();
      const { error: updateError } = await supabase
        .from('account_opportunities')
        .update({
          stage_id: newStageId,
          status: 'open',
          closed_at: null,
          loss_reason_id: null,
          updated_at: updatedAt
        })
        .eq('id', opportunityId);

      if (updateError) throw updateError;

      setOpportunities(prev =>
        prev.map(opp =>
          opp.id === opportunityId
            ? {
                ...opp,
                stage_id: newStageId,
                status: 'open' as OpportunityStatus,
                closed_at: null,
                loss_reason_id: null,
                updated_at: updatedAt
              }
            : opp
        )
      );

      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  const reorderOpportunities = async (reorderedItems: { id: string; order_index: number }[]) => {
    if (!supabase) return false;

    try {
      for (const item of reorderedItems) {
        const { error: updateError } = await supabase
          .from('account_opportunities')
          .update({ order_index: item.order_index, updated_at: new Date().toISOString() })
          .eq('id', item.id);

        if (updateError) throw updateError;
      }

      setOpportunities(prev => {
        const updated = [...prev];
        for (const item of reorderedItems) {
          const idx = updated.findIndex(o => o.id === item.id);
          if (idx !== -1) {
            updated[idx] = { ...updated[idx], order_index: item.order_index };
          }
        }
        return updated;
      });

      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  useEffect(() => {
    fetchOpportunities();
  }, [fetchOpportunities]);

  return {
    opportunities,
    loading,
    error,
    loadingProgress,
    fetchOpportunities,
    createOpportunity,
    checkExistingOpportunity,
    updateOpportunity,
    deleteOpportunity,
    moveOpportunity,
    closeOpportunity,
    reopenOpportunity,
    reorderOpportunities
  };
}
